import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Divider
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import axios from 'axios';
import { WooCommerceOrderItem } from '../../../types/dashboard';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const WooCommerceOrdersWidget: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<WooCommerceOrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/dashboard/woocommerce-orders');
      setData(response.data);
      setError(null);
    } catch (err) {
      setError('فشل في تحميل طلبات WooCommerce');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order: WooCommerceOrderItem) => {
    navigate('/invoices/woocommerce/orders');
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
      case 'قيد الانتظار':
        return '#f39c12';
      case 'processing':
      case 'قيد المعالجة':
        return '#3498db';
      case 'completed':
      case 'مكتمل':
        return '#27ae60';
      default:
        return '#95a5a6';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'قيد الانتظار';
      case 'processing':
        return 'قيد المعالجة';
      case 'completed':
        return 'مكتمل';
      case 'on-hold':
        return 'معلق';
      case 'cancelled':
        return 'ملغي';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', p: 3, minHeight: 300 }}>
        <Typography color="error">{error}</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderTop: '4px solid #7c3aed',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              mr: 2
            }}
          >
            🛒
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 0.5 }}>
              طلبات WooCommerce
            </Typography>
            <Typography variant="body2" sx={{ color: '#95a5a6' }}>
              <Chip 
                label={`${data.length} طلب`} 
                size="small" 
                sx={{ 
                  bgcolor: '#ede7f6', 
                  color: '#7c3aed',
                  fontWeight: 'bold'
                }} 
              /> مستورد ومعلق
            </Typography>
          </Box>
        </Box>

        {/* Orders List */}
        {data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h3" sx={{ opacity: 0.3, mb: 2 }}>✅</Typography>
            <Typography color="textSecondary">لا توجد طلبات معلقة</Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {data.slice(0, 5).map((order, index) => (
              <React.Fragment key={order._id}>
                {index > 0 && <Divider sx={{ my: 1 }} />}
                <ListItem
                  sx={{
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#e9ecef',
                      transform: 'translateX(-5px)'
                    }
                  }}
                  onClick={() => handleViewOrder(order)}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                        طلب #{order.orderNumber}
                      </Typography>
                      <Chip
                        label={getStatusLabel(order.status)}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(order.status),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#95a5a6', display: 'block' }}>
                          👤 {order.customerFullName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#95a5a6', display: 'block' }}>
                          📅 {(() => {
                            try {
                              return format(new Date(order.dateCreated), 'yyyy-MM-dd', { locale: ar });
                            } catch {
                              return order.dateCreated.split('T')[0];
                            }
                          })()}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#7c3aed' }}>
                          {order.totals.total.toLocaleString('ar-SA')} {order.totals.currency}
                        </Typography>
                        {order.convertedToInvoice && (
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            label="تم التحويل"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: '#d4edda',
                              color: '#155724',
                              mt: 0.5
                            }}
                          />
                        )}
                        {order.shippingBillNumber && (
                          <Chip
                            icon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
                            label={order.shippingBillNumber}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: '#cce5ff',
                              color: '#004085',
                              mt: 0.5,
                              ml: 0.5
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}

        {data.length > 5 && (
          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/invoices/woocommerce/orders')}
            sx={{
              mt: 2,
              borderColor: '#7c3aed',
              color: '#7c3aed',
              fontWeight: 'bold',
              '&:hover': {
                borderColor: '#6d28d9',
                bgcolor: 'rgba(124, 58, 237, 0.05)'
              }
            }}
          >
            عرض جميع الطلبات ({data.length})
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default WooCommerceOrdersWidget;
